import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  EnvelopeIcon,
  PaperClipIcon,
  UserIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  DocumentIcon,
  PhotoIcon,
  MusicalNoteIcon,
  PlayIcon,
  PauseIcon,
  ArrowsRightLeftIcon,
  Bars3Icon,
  ArrowRightCircleIcon,
  ArrowLeftCircleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  defaultDropAnimationSideEffects,
  type DragEndEvent,
  type DragStartEvent,
  type DropAnimation,
} from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import type { TrackerEmail, EmailAttachment } from '../../tracker/types/tracker.types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { sanitizeEmailHtml } from '@/core/utils/sanitizeHtml';
import './EmailContentModal.css';

interface EmailContentModalProps {
  email: TrackerEmail;
  onClose: () => void;
  // Optional props for email reallocation
  currentAffaireId?: number;
  onReallocate?: (email: TrackerEmail) => void;
}

export const EmailContentModal: React.FC<EmailContentModalProps> = ({
  email,
  onClose,
  currentAffaireId,
  onReallocate
}) => {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [processedHtml, setProcessedHtml] = useState<string | null>(null);
  const [inlineImageUrls, setInlineImageUrls] = useState<Map<string, string>>(new Map());
  // Local override on top of email.excluded_attachments / is_inline,
  // updated optimistically when the user drags between sections.
  const [excludedOverride, setExcludedOverride] = useState<Set<string> | null>(null);
  const [inlineOverride, setInlineOverride] = useState<Record<string, boolean>>({});
  const [activeDrag, setActiveDrag] = useState<{ uniqueId: string; idKey: string; isInline: boolean; attachment: EmailAttachment } | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'EEEE d MMMM yyyy à HH:mm', { locale: fr });
    } catch {
      return dateString;
    }
  };

  const getAuthToken = (): string | null => {
    return localStorage.getItem('authToken');
  };

  const getDownloadUrl = (index: number, inline: boolean = false) => {
    const baseUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:10007'}/api/admin/emails/${email.id}/attachments/${index}/download`;
    return inline ? `${baseUrl}?inline=true` : baseUrl;
  };

  const fetchWithAuth = async (url: string): Promise<Blob> => {
    const token = getAuthToken();
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }
    return response.blob();
  };

  const isImageFile = (contentType: string) => contentType?.startsWith('image/');
  const isPdfFile = (contentType: string) => contentType === 'application/pdf';
  const isAudioFile = (contentType: string, name: string) => {
    const ext = name?.toLowerCase().split('.').pop() || '';
    return contentType?.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a'].includes(ext);
  };

  // Extract all CID references from HTML content for fallback detection
  const extractCidsFromHtml = (html: string | null): Set<string> => {
    if (!html) return new Set();
    const cids = new Set<string>();
    // Match cid: references in various formats
    const cidRegex = /cid:([^"'\s>]+)/gi;
    let match;
    while ((match = cidRegex.exec(html)) !== null) {
      const cid = match[1];
      cids.add(cid.toLowerCase());
      // Also extract the filename part before @ (e.g., "image004.png" from "image004.png@01DC5E2E")
      const atIndex = cid.indexOf('@');
      if (atIndex > 0) {
        cids.add(cid.substring(0, atIndex).toLowerCase());
      }
    }
    return cids;
  };

  // Get HTML content - be more permissive to catch CID references
  // Check full_content_html first, regardless of body_content_type flag
  const htmlContent = email.full_content_html || null;
  const htmlCids = extractCidsFromHtml(htmlContent);

  // Safe access to attachments array
  const attachments = email.attachments || [];

  // 2026-05-28 — trust the DB's `is_inline` / `isInline` flag (set by
  // sync_emails.py from Microsoft Graph). That's what `useAllAttachments`
  // uses to decide what lands in the affaire's Documents tab. Previously
  // this modal had an aggressive heuristic that marked EVERY image as
  // inline whenever any `cid:` reference existed in the HTML — which
  // wrongly hid real attachments (e.g. a `rapport electrique.jpeg` that
  // is in the dossier but also has signature CIDs alongside). The badge
  // would then read "Dans l'email" while the docs tab said otherwise.
  // Only fall back to CID-based inference when the flag is undefined
  // on BOTH camelCase and snake_case for legacy rows that pre-date the
  // sync writing the flag.
  // Build the same uniqueId scheme as the backend / useAllAttachments.
  // For non-inline attachments: `attachment-${nonInlineIdx}-${name}`.
  // For inline attachments: fall back to att.id when present, else a
  // stable composite of name+size (jsonb path can't reorder them).
  const getUniqueId = (attachment: EmailAttachment, nonInlineIdx: number, isInline: boolean): string => {
    if (attachment.id) return attachment.id as string;
    if (!isInline) return `attachment-${nonInlineIdx}-${attachment.name || ''}`;
    return `inline-${attachment.name || ''}-${attachment.size || 0}`;
  };

  const isInlineAttachment = (attachment: EmailAttachment): boolean => {
    // Local override (set when the user drags a "Visuel" → "Au dossier")
    const idKey = (attachment.id as string | undefined) || `${attachment.name}-${attachment.size}`;
    if (Object.prototype.hasOwnProperty.call(inlineOverride, idKey)) {
      return inlineOverride[idKey];
    }
    if (attachment.isInline === true || attachment.is_inline === true) return true;
    if (attachment.isInline === false || attachment.is_inline === false) return false;
    // Fallback for legacy rows with neither flag set: only mark as inline
    // if the attachment's name or contentId actually appears in an HTML
    // cid: reference (no aggressive blanket reclassification).
    if (htmlCids.size === 0) return false;
    const contentType = attachment.contentType || attachment.content_type || '';
    if (isPdfFile(contentType) || attachment.name?.toLowerCase().endsWith('.pdf')) return false;
    if (!isImageFile(contentType)) return false;
    const cidRaw = attachment.contentId || attachment.content_id || '';
    const cid = cidRaw.replace(/^<|>$/g, '').toLowerCase();
    if (cid && (htmlCids.has(cid) || htmlCids.has(cid.split('@')[0]))) return true;
    const name = (attachment.name || '').toLowerCase();
    if (name) {
      for (const refCid of htmlCids) {
        if (refCid === name || refCid.split('@')[0] === name) return true;
      }
    }
    return false;
  };

  // Compute once per render so the cid-rewriting effect agrees with the
  // section partitioning below.
  const inlineAttachmentIndices = new Set<number>(
    attachments.map((att, idx) => (isInlineAttachment(att) ? idx : -1)).filter((i) => i !== -1)
  );

  // Get content ID for attachment (strip angle brackets if present)
  const getContentId = (attachment: EmailAttachment): string | null => {
    const cid = attachment.contentId || attachment.content_id;
    if (cid) {
      // Remove angle brackets if present: <image001.png> -> image001.png
      return cid.replace(/^<|>$/g, '');
    }
    // Fallback: try to find matching CID from HTML for the attachment name
    if (htmlCids.size > 0 && attachment.name) {
      const attachmentName = attachment.name.toLowerCase();
      for (const cid of htmlCids) {
        if (cid.includes(attachmentName) || cid.split('@')[0] === attachmentName) {
          return cid;
        }
      }
    }
    return null;
  };

  // Filter attachments to show only non-inline ones
  const regularAttachments = attachments.filter(att => !isInlineAttachment(att));

  // Get inline attachments with their content IDs
  const inlineAttachments = attachments
    .map((att, index) => ({ attachment: att, originalIndex: index }))
    .filter(({ attachment }) => isInlineAttachment(attachment));

  const canPreview = (attachment: EmailAttachment) => {
    const contentType = attachment.contentType || attachment.content_type || '';
    return isImageFile(contentType) || isPdfFile(contentType) || isAudioFile(contentType, attachment.name);
  };

  const getFileIcon = (contentType: string, name: string) => {
    if (contentType?.startsWith('image/')) return <PhotoIcon className="w-5 h-5" />;
    if (contentType?.startsWith('audio/') || isAudioFile(contentType, name)) return <MusicalNoteIcon className="w-5 h-5" />;
    return <DocumentIcon className="w-5 h-5" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  // Fetch inline images and process HTML content on mount
  useEffect(() => {
    if (!htmlContent || inlineAttachments.length === 0) {
      setProcessedHtml(htmlContent);
      return;
    }

    const fetchInlineImages = async () => {
      const urlMap = new Map<string, string>();
      const attachmentNameToUrl = new Map<string, string>();
      const allBlobUrls: string[] = [];

      // Fetch all inline images in parallel
      await Promise.all(
        inlineAttachments.map(async ({ attachment, originalIndex }) => {
          try {
            const url = getDownloadUrl(originalIndex, true);
            const blob = await fetchWithAuth(url);
            const blobUrl = URL.createObjectURL(blob);

            allBlobUrls.push(blobUrl);

            // Store by contentId if available
            const contentId = getContentId(attachment);
            if (contentId) {
              urlMap.set(contentId.toLowerCase(), blobUrl);
            }
            // Also store by attachment name for fallback matching
            if (attachment.name) {
              attachmentNameToUrl.set(attachment.name.toLowerCase(), blobUrl);
            }
          } catch (error) {
            console.error(`Failed to fetch inline image ${attachment.name}:`, error);
          }
        })
      );

      setInlineImageUrls(urlMap);

      // Replace cid: references in HTML with blob URLs
      let processedContent = htmlContent;

      // Track which blob URLs have been used
      const usedBlobUrls = new Set<string>();
      // Track unmatched CIDs for position-based matching
      const unmatchedCids: string[] = [];

      // First pass: replace CIDs that we can match by name/contentId
      processedContent = processedContent.replace(/cid:([^"'\s>]+)/gi, (match, cidRef) => {
        const cidLower = cidRef.toLowerCase();

        // Try exact match first
        if (urlMap.has(cidLower)) {
          const url = urlMap.get(cidLower)!;
          usedBlobUrls.add(url);
          return url;
        }

        // Try matching by filename part (before @)
        const atIndex = cidLower.indexOf('@');
        if (atIndex > 0) {
          const filenamePart = cidLower.substring(0, atIndex);
          if (attachmentNameToUrl.has(filenamePart)) {
            const url = attachmentNameToUrl.get(filenamePart)!;
            usedBlobUrls.add(url);
            return url;
          }
        }

        // Try to find attachment name that matches
        for (const [name, blobUrl] of attachmentNameToUrl) {
          if ((cidLower.includes(name) || name.includes(cidLower.split('@')[0])) && !usedBlobUrls.has(blobUrl)) {
            usedBlobUrls.add(blobUrl);
            return blobUrl;
          }
        }

        // Track as unmatched for second pass
        unmatchedCids.push(cidRef);
        return match;
      });

      // Second pass: match remaining CIDs with unused blob URLs by position
      if (unmatchedCids.length > 0) {
        const unusedBlobUrls = allBlobUrls.filter(url => !usedBlobUrls.has(url));

        // If we have unmatched CIDs and unused images, try position-based matching
        unmatchedCids.forEach((cidRef, index) => {
          if (index < unusedBlobUrls.length) {
            const blobUrl = unusedBlobUrls[index];
            // Escape special regex characters in CID
            const escapedCid = cidRef.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const cidPattern = new RegExp(`cid:${escapedCid}`, 'gi');
            processedContent = processedContent.replace(cidPattern, blobUrl);
          }
        });
      }

      setProcessedHtml(processedContent);
    };

    fetchInlineImages();

    // Cleanup blob URLs on unmount
    return () => {
      inlineImageUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [email.id]); // Re-run when email changes

  const loadPreview = async (index: number) => {
    const attachment = attachments[index];
    if (!attachment) return;

    setPreviewLoading(true);
    setPreviewIndex(index);

    // Stop any playing audio
    if (audioElement) {
      audioElement.pause();
      setAudioElement(null);
      setIsPlaying(false);
    }

    try {
      const url = getDownloadUrl(index, true);
      const blob = await fetchWithAuth(url);
      const blobUrl = URL.createObjectURL(blob);

      // Clean up previous URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(blobUrl);

      // If audio, create audio element
      const contentType = attachment.contentType || attachment.content_type || '';
      if (isAudioFile(contentType, attachment.name)) {
        const audio = new Audio(blobUrl);
        audio.addEventListener('ended', () => setIsPlaying(false));
        setAudioElement(audio);
      }
    } catch (error) {
      console.error('Preview error:', error);
      alert('Erreur lors du chargement de l\'aperçu');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    if (audioElement) {
      audioElement.pause();
    }
    setPreviewIndex(null);
    setPreviewUrl(null);
    setAudioElement(null);
    setIsPlaying(false);
  };

  // Navigate to previous regular attachment
  const goToPrevious = () => {
    if (previewIndex === null) return;
    const currentRegularIndex = regularAttachmentIndices.indexOf(previewIndex);
    if (currentRegularIndex <= 0) return;
    loadPreview(regularAttachmentIndices[currentRegularIndex - 1]);
  };

  // Navigate to next regular attachment
  const goToNext = () => {
    if (previewIndex === null) return;
    const currentRegularIndex = regularAttachmentIndices.indexOf(previewIndex);
    if (currentRegularIndex < 0 || currentRegularIndex >= regularAttachmentIndices.length - 1) return;
    loadPreview(regularAttachmentIndices[currentRegularIndex + 1]);
  };

  // Get current position in regular attachments for display
  const getCurrentRegularIndex = () => {
    if (previewIndex === null) return -1;
    return regularAttachmentIndices.indexOf(previewIndex);
  };

  // Compute the effective excluded set (DB row + local overrides) once.
  const baseExcluded = new Set<string>(((email as any).excluded_attachments as string[] | undefined) || []);
  const effectiveExcluded: Set<string> = excludedOverride ?? baseExcluded;

  // DnD: 5 px drag threshold so a click still triggers preview.
  // Keyboard sensor enables Tab → focus card, Space → pick up, arrow keys → move,
  // Space → drop. Free accessibility.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // Smooth animation when the dragged card lands in its target zone.
  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: { active: { opacity: '0.3' } },
    }),
  };

  // Flash a brief highlight on a card after the user moves it via the
  // quick-arrow buttons (no drag animation to convey the change).
  const flash = (key: string) => {
    setFlashKey(key);
    window.setTimeout(() => setFlashKey((k) => (k === key ? null : k)), 700);
  };

  const queryClient = useQueryClient();
  const persistInclusion = async (next: { excluded?: string[]; inlineOverrides?: Record<string, boolean> }) => {
    try {
      const token = localStorage.getItem('authToken');
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:10007';
      const body = {
        excluded_attachments: next.excluded,
        inline_overrides: next.inlineOverrides,
      };
      const res = await fetch(`${baseURL}/api/admin/emails/${email.id}/inclusion`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`PATCH inclusion ${res.status}`);
      // Invalidate the affaire's emails query so the Documents tab
      // re-fetches and reflects the new inclusion state on next render.
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-emails'] });
    } catch (err) {
      console.error('persistInclusion error', err);
    }
  };

  // Move an attachment between sections. Used by both DnD and the
  // quick-arrow buttons. `target` = where to move TO.
  const moveAttachment = (
    target: 'docs' | 'decor',
    uid: string,
    isInline: boolean,
    idKey: string,
  ) => {
    const updateExcluded = (mutate: (prev: Set<string>) => Set<string>, extraInline?: Record<string, boolean>) => {
      setExcludedOverride((prev) => {
        const base = prev ?? new Set<string>(((email as any).excluded_attachments as string[] | undefined) || []);
        const next = mutate(new Set(base));
        persistInclusion({
          excluded: Array.from(next),
          inlineOverrides: extraInline,
        });
        return next;
      });
    };
    if (target === 'docs') {
      if (isInline) {
        const overrideKey = idKey || uid;
        setInlineOverride((prev) => ({ ...prev, [overrideKey]: false }));
        updateExcluded((s) => { s.delete(uid); return s; }, { [overrideKey]: false });
        flash(uid);
      } else if (effectiveExcluded.has(uid)) {
        updateExcluded((s) => { s.delete(uid); return s; });
        flash(uid);
      }
    } else if (target === 'decor') {
      if (isInline) return;
      if (effectiveExcluded.has(uid)) return;
      updateExcluded((s) => { s.add(uid); return s; });
      flash(uid);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data?.current as { uniqueId?: string; idKey?: string; isInline?: boolean; attachment?: EmailAttachment } | undefined;
    if (data?.uniqueId && data.attachment) {
      setActiveDrag({
        uniqueId: data.uniqueId,
        idKey: data.idKey || data.uniqueId,
        isInline: !!data.isInline,
        attachment: data.attachment,
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    const target = String(over.id) as 'docs' | 'decor';
    const meta = (active.data?.current ?? {}) as {
      uniqueId?: string;
      isInline?: boolean;
      idKey?: string;
    };
    if (!meta.uniqueId) return;
    moveAttachment(target, meta.uniqueId, !!meta.isInline, meta.idKey || meta.uniqueId);
  };

  const handleDragCancel = () => setActiveDrag(null);

  const handleDownload = async (index: number, filename: string) => {
    try {
      const url = getDownloadUrl(index, false);
      const blob = await fetchWithAuth(url);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Download error:', error);
      alert('Erreur lors du téléchargement');
    }
  };

  const toggleAudio = () => {
    if (!audioElement) return;
    if (isPlaying) {
      audioElement.pause();
      setIsPlaying(false);
    } else {
      audioElement.play();
      setIsPlaying(true);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (previewIndex !== null) {
        if (e.key === 'Escape') closePreview();
        if (e.key === 'ArrowLeft') goToPrevious();
        if (e.key === 'ArrowRight') goToNext();
      } else {
        if (e.key === 'Escape') onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewIndex, onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (audioElement) audioElement.pause();
    };
  }, []);

  // Use processed HTML (with inline images replaced) or fallback to raw htmlContent
  const displayHtml = processedHtml !== null ? processedHtml : htmlContent;
  const textContent = email.full_content || email.body_preview || '';

  // Map regular attachment index to original attachments index
  const regularAttachmentIndices = attachments
    .map((att, idx) => ({ attachment: att, originalIndex: idx }))
    .filter(({ attachment }) => !isInlineAttachment(attachment))
    .map(({ originalIndex }) => originalIndex);

  const currentAttachment = previewIndex !== null ? attachments[previewIndex] : null;

  return (
    <div className="email-modal-overlay" onClick={onClose}>
      <div className="email-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="email-modal-header">
          <div className="email-modal-header-icon">
            <EnvelopeIcon />
          </div>
          <div className="email-modal-header-content">
            <h2 className="email-modal-title">{email.subject || '(Sans objet)'}</h2>
            <div className="email-modal-meta">
              <span className="email-modal-date">
                <CalendarIcon />
                {formatDate(email.email_time || email.received_time || '')}
              </span>
              {email.has_attachments && attachments.length > 0 && (
                <span className="email-modal-attachments">
                  <PaperClipIcon />
                  {attachments.length} pièce{attachments.length > 1 ? 's' : ''} jointe{attachments.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <button className="email-modal-close" onClick={onClose} title="Fermer (Échap)">
            <XMarkIcon />
          </button>
        </div>

        {/* Sender/Receiver Info */}
        <div className="email-modal-info">
          <div className="email-modal-info-row">
            <span className="email-modal-info-label">De:</span>
            <span className="email-modal-info-value">
              <UserIcon />
              {email.sender_name ? `${email.sender_name} <${email.sender_address}>` : email.sender_address}
            </span>
          </div>
          {(email.recipients_to && email.recipients_to.length > 0) ? (
            <div className="email-modal-info-row">
              <span className="email-modal-info-label">À:</span>
              <span className="email-modal-info-value">
                {email.recipients_to.map(r => r.name ? `${r.name} <${r.address}>` : r.address).join(', ')}
              </span>
            </div>
          ) : email.receiver_address ? (
            <div className="email-modal-info-row">
              <span className="email-modal-info-label">À:</span>
              <span className="email-modal-info-value">
                {email.receiver_name ? `${email.receiver_name} <${email.receiver_address}>` : email.receiver_address}
              </span>
            </div>
          ) : null}
          {email.recipients_cc && email.recipients_cc.length > 0 && (
            <div className="email-modal-info-row">
              <span className="email-modal-info-label">CC:</span>
              <span className="email-modal-info-value">
                {email.recipients_cc.map(r => r.name ? `${r.name} <${r.address}>` : r.address).join(', ')}
              </span>
            </div>
          )}
          {/* BCC only makes sense for emails we sent — for received emails the
              recipient never sees who else was BCC'd (data isn't available). */}
          {email.email_type === 'sent' && email.recipients_bcc && email.recipients_bcc.length > 0 && (
            <div className="email-modal-info-row">
              <span className="email-modal-info-label">Cci:</span>
              <span className="email-modal-info-value">
                {email.recipients_bcc.map(r => r.name ? `${r.name} <${r.address}>` : r.address).join(', ')}
              </span>
            </div>
          )}
        </div>

        {/* Main content — 2 columns: email body LEFT, attachments RIGHT.
            When the email has no attachments the right column collapses
            and the body takes the full width (handled in CSS). */}
        <div
          className={`email-modal-main ${(regularAttachments.length + inlineAttachments.length) === 0 ? 'email-modal-main--no-pj' : ''}`}
        >
          <div className="email-modal-body">
            {displayHtml ? (
              <div
                className="email-modal-html-content"
                dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(displayHtml) }}
              />
            ) : (
              <pre className="email-modal-text-content">{textContent}</pre>
            )}
          </div>

          {(regularAttachments.length + inlineAttachments.length) > 0 && (
            <DndContext
              sensors={dndSensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <aside className={`email-modal-pj-panel ${activeDrag ? 'email-modal-pj-panel--dragging' : ''}`}>
                {/* Compute per-section lists after applying the local
                    excluded override. A non-inline attachment that's in
                    `excluded_attachments` migrates from "Au dossier" to
                    "Dans l'email" — same way useAllAttachments filters it
                    from the Docs tab. */}
                {(() => {
                  const docsList: Array<{ attachment: EmailAttachment; originalIndex: number; uniqueId: string; idKey: string; isInline: boolean }> = [];
                  const decorList: Array<{ attachment: EmailAttachment; originalIndex: number; uniqueId: string; idKey: string; isInline: boolean }> = [];

                  let nonInlineCounter = 0;
                  attachments.forEach((att, idx) => {
                    const inline = isInlineAttachment(att);
                    const uniqueId = getUniqueId(att, nonInlineCounter, inline);
                    const idKey = (att.id as string | undefined) || `${att.name}-${att.size}`;
                    if (!inline) nonInlineCounter++;
                    const entry = { attachment: att, originalIndex: idx, uniqueId, idKey, isInline: inline };
                    if (!inline && !effectiveExcluded.has(uniqueId)) docsList.push(entry);
                    else decorList.push(entry);
                  });

                  return (
                    <>
                      <PJSection
                        id="docs"
                        title={`${docsList.length} document${docsList.length > 1 ? 's' : ''}`}
                        hint="ajoutés au dossier"
                        emptyHint="Glissez une pièce ici · ou cliquez ←"
                        icon={<PaperClipIcon className="w-4 h-4" />}
                        tone="docs"
                        items={docsList}
                        canPreview={canPreview}
                        getFileIcon={getFileIcon}
                        formatFileSize={formatFileSize}
                        loadPreview={loadPreview}
                        handleDownload={handleDownload}
                        sideBadge="Au dossier"
                        moveAttachment={moveAttachment}
                        flashKey={flashKey}
                        activeDragId={activeDrag?.uniqueId ?? null}
                      />
                      <PJSection
                        id="decor"
                        title={`${decorList.length} visuel${decorList.length > 1 ? 's' : ''} du message`}
                        hint="non ajoutés au dossier"
                        emptyHint="Tout est dans le dossier"
                        icon={<PhotoIcon className="w-4 h-4" />}
                        tone="decor"
                        items={decorList}
                        canPreview={canPreview}
                        getFileIcon={getFileIcon}
                        formatFileSize={formatFileSize}
                        loadPreview={loadPreview}
                        handleDownload={handleDownload}
                        sideBadge="Dans l'email"
                        moveAttachment={moveAttachment}
                        flashKey={flashKey}
                        activeDragId={activeDrag?.uniqueId ?? null}
                      />
                    </>
                  );
                })()}
              </aside>
              <DragOverlay dropAnimation={dropAnimation}>
                {activeDrag ? (
                  <div className={`attachment-card attachment-card--overlay attachment-card--${activeDrag.isInline ? 'decor' : 'in-dossier'}`}>
                    <div className="attachment-card-icon">
                      {getFileIcon(activeDrag.attachment.contentType || activeDrag.attachment.content_type || '', activeDrag.attachment.name)}
                    </div>
                    <div className="attachment-card-info">
                      <span className="attachment-card-name">{activeDrag.attachment.name}</span>
                      <span className="attachment-card-size">{formatFileSize(activeDrag.attachment.size)}</span>
                    </div>
                    <span className="attachment-card-overlay-hint">Glissez vers la zone cible…</span>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>

        {/* Footer with Actions - Only show if reallocation is available */}
        {onReallocate && currentAffaireId && (
          <div className="email-modal-footer">
            <div className="email-modal-footer-info">
              <span className="email-modal-current-affaire">
                Dossier actuel: <strong>#{currentAffaireId}</strong>
              </span>
            </div>
            <button
              className="email-modal-reallocate-btn"
              onClick={() => {
                onClose();
                onReallocate(email);
              }}
            >
              <ArrowsRightLeftIcon className="w-4 h-4" />
              Déplacer vers un autre dossier
            </button>
          </div>
        )}
      </div>

      {/* (Preview overlay below) */}
      {/* Attachment Preview Overlay */}
      {previewIndex !== null && currentAttachment && (
        <div className="attachment-preview-overlay" onClick={closePreview}>
          <div className="attachment-preview-container" onClick={(e) => e.stopPropagation()}>
            {/* Preview Header */}
            <div className="attachment-preview-header">
              <div className="attachment-preview-title">
                {getFileIcon(currentAttachment.contentType || currentAttachment.content_type || '', currentAttachment.name)}
                <span>{currentAttachment.name}</span>
                <span className="attachment-preview-counter">
                  ({getCurrentRegularIndex() + 1} / {regularAttachments.length})
                </span>
              </div>
              <div className="attachment-preview-actions">
                <button
                  onClick={() => handleDownload(previewIndex, currentAttachment.name)}
                  className="preview-action-btn"
                  title="Télécharger"
                >
                  <ArrowDownTrayIcon className="w-5 h-5" />
                </button>
                <button onClick={closePreview} className="preview-close-btn" title="Fermer (Échap)">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="attachment-preview-content">
              {previewLoading ? (
                <div className="preview-loading">
                  <div className="preview-spinner"></div>
                  <span>Chargement...</span>
                </div>
              ) : previewUrl && (
                <>
                  {isImageFile(currentAttachment.contentType || currentAttachment.content_type || '') && (
                    <img src={previewUrl} alt={currentAttachment.name} className="preview-image" />
                  )}
                  {isPdfFile(currentAttachment.contentType || currentAttachment.content_type || '') && (
                    <iframe src={previewUrl} className="preview-pdf" title={currentAttachment.name} />
                  )}
                  {isAudioFile(currentAttachment.contentType || currentAttachment.content_type || '', currentAttachment.name) && (
                    <div className="preview-audio">
                      <div className="audio-icon">
                        <MusicalNoteIcon className="w-16 h-16" />
                      </div>
                      <div className="audio-name">{currentAttachment.name}</div>
                      <button onClick={toggleAudio} className="audio-play-btn">
                        {isPlaying ? <PauseIcon className="w-8 h-8" /> : <PlayIcon className="w-8 h-8" />}
                      </button>
                      {audioElement && (
                        <audio
                          controls
                          src={previewUrl}
                          className="audio-controls"
                          onPlay={() => setIsPlaying(true)}
                          onPause={() => setIsPlaying(false)}
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Navigation Arrows - Only show if more than one regular attachment */}
            {regularAttachments.length > 1 && (
              <>
                <button
                  className={`preview-nav-btn prev ${getCurrentRegularIndex() === 0 ? 'disabled' : ''}`}
                  onClick={goToPrevious}
                  disabled={getCurrentRegularIndex() === 0}
                  title="Précédent (←)"
                >
                  <ChevronLeftIcon className="w-8 h-8" />
                </button>
                <button
                  className={`preview-nav-btn next ${getCurrentRegularIndex() === regularAttachments.length - 1 ? 'disabled' : ''}`}
                  onClick={goToNext}
                  disabled={getCurrentRegularIndex() === regularAttachments.length - 1}
                  title="Suivant (→)"
                >
                  <ChevronRightIcon className="w-8 h-8" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── DnD helpers: PJSection (droppable) + DraggableCard ──────── */

interface PJItem {
  attachment: EmailAttachment;
  originalIndex: number;
  uniqueId: string;
  idKey: string;
  isInline: boolean;
}

interface PJSectionProps {
  id: 'docs' | 'decor';
  title: string;
  hint: string;
  emptyHint: string;
  icon: React.ReactNode;
  tone: 'docs' | 'decor';
  items: PJItem[];
  canPreview: (a: EmailAttachment) => boolean;
  getFileIcon: (contentType: string, name: string) => React.ReactNode;
  formatFileSize: (bytes: number) => string;
  loadPreview: (originalIndex: number) => void;
  handleDownload: (originalIndex: number, filename: string) => void;
  sideBadge: string;
  moveAttachment: (target: 'docs' | 'decor', uid: string, isInline: boolean, idKey: string) => void;
  flashKey: string | null;
  activeDragId: string | null;
}

const PJSection: React.FC<PJSectionProps> = ({
  id, title, hint, emptyHint, icon, tone, items, canPreview, getFileIcon, formatFileSize, loadPreview, handleDownload, sideBadge, moveAttachment, flashKey, activeDragId,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={[
        'email-modal-attachments-section',
        tone === 'docs' ? 'email-modal-section--docs' : 'email-modal-section--decor email-modal-inline-section',
        isOver ? 'email-modal-section--drop-over' : '',
        activeDragId ? 'email-modal-section--armed' : '',
      ].join(' ').trim()}
      data-drop-target={tone}
    >
      <div className={`attachments-label${tone === 'decor' ? ' inline-label' : ''}`}>
        {icon}
        <span>{title}</span>
        <span className="attachments-label__hint">{hint}</span>
      </div>
      <div className={`attachments-grid${tone === 'decor' ? ' inline-grid' : ''}`}>
        {items.length === 0 ? (
          <div className="email-modal-section__empty">{emptyHint}</div>
        ) : items.map((it) => (
          <PJCard
            key={it.uniqueId + '|' + it.originalIndex}
            item={it}
            tone={tone}
            sideBadge={sideBadge}
            canPreview={canPreview}
            getFileIcon={getFileIcon}
            formatFileSize={formatFileSize}
            loadPreview={loadPreview}
            handleDownload={handleDownload}
            moveAttachment={moveAttachment}
            flashed={flashKey === it.uniqueId}
          />
        ))}
      </div>
    </div>
  );
};

interface PJCardProps {
  item: PJItem;
  tone: 'docs' | 'decor';
  sideBadge: string;
  canPreview: (a: EmailAttachment) => boolean;
  getFileIcon: (contentType: string, name: string) => React.ReactNode;
  formatFileSize: (bytes: number) => string;
  loadPreview: (originalIndex: number) => void;
  handleDownload: (originalIndex: number, filename: string) => void;
  moveAttachment: (target: 'docs' | 'decor', uid: string, isInline: boolean, idKey: string) => void;
  flashed: boolean;
}

const PJCard: React.FC<PJCardProps> = ({ item, tone, sideBadge, canPreview, getFileIcon, formatFileSize, loadPreview, handleDownload, moveAttachment, flashed }) => {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `${item.uniqueId}|${item.originalIndex}`,
    data: { uniqueId: item.uniqueId, isInline: item.isInline, idKey: item.idKey, attachment: item.attachment },
  });
  const contentType = item.attachment.contentType || item.attachment.content_type || '';
  const previewable = canPreview(item.attachment);
  // Where would the quick-move arrow send this card? The other section.
  const otherSide: 'docs' | 'decor' = tone === 'docs' ? 'decor' : 'docs';
  return (
    <div
      ref={setNodeRef}
      className={[
        'attachment-card',
        tone === 'docs' ? 'attachment-card--in-dossier' : 'inline-card attachment-card--decor',
        previewable ? 'previewable' : '',
        isDragging ? 'attachment-card--dragging' : '',
        flashed ? 'attachment-card--flash' : '',
      ].join(' ').trim()}
      onClick={(e) => {
        // Clicks (no drag past 5 px) still trigger preview. Buttons opt out.
        const target = e.target as HTMLElement;
        if (target.closest('button, .attachment-card-grip')) return;
        if (previewable) loadPreview(item.originalIndex);
      }}
      title={previewable ? 'Cliquez pour prévisualiser · glissez ou utilisez la flèche pour déplacer' : item.attachment.name}
    >
      <span
        className="attachment-card-grip"
        {...listeners}
        {...attributes}
        title="Glissez pour déplacer"
        aria-label="Poignée de déplacement"
      >
        <Bars3Icon className="w-3.5 h-3.5" />
      </span>
      <div className={`attachment-card-icon${tone === 'decor' ? ' inline-icon' : ''}`}>{getFileIcon(contentType, item.attachment.name)}</div>
      <div className="attachment-card-info">
        <span className="attachment-card-name">{item.attachment.name}</span>
        <span className="attachment-card-size">{formatFileSize(item.attachment.size)}</span>
      </div>
      <span
        className={`attachment-card-badge ${tone === 'docs' ? 'attachment-card-badge--in' : 'attachment-card-badge--out'}`}
        title={tone === 'docs' ? 'Sera ajouté au dossier' : "Reste dans l'email, pas dans le dossier"}
      >
        <span className="attachment-card-badge-dot" /> {sideBadge}
      </span>
      <div className="attachment-card-actions">
        {previewable && (
          <button
            className="attachment-card-preview-btn"
            onClick={(e) => { e.stopPropagation(); loadPreview(item.originalIndex); }}
            title="Aperçu"
            aria-label="Aperçu"
          >
            <EyeIcon className="w-4 h-4" />
          </button>
        )}
        <button
          className="attachment-card-quick-move"
          onClick={(e) => {
            e.stopPropagation();
            moveAttachment(otherSide, item.uniqueId, item.isInline, item.idKey);
          }}
          title={tone === 'docs' ? "Retirer du dossier (déplacer vers l'email)" : 'Ajouter au dossier'}
          aria-label={tone === 'docs' ? 'Retirer du dossier' : 'Ajouter au dossier'}
        >
          {tone === 'docs'
            ? <ArrowRightCircleIcon className="w-4 h-4" />
            : <ArrowLeftCircleIcon className="w-4 h-4" />}
        </button>
        <button
          className="attachment-card-download"
          onClick={(e) => { e.stopPropagation(); handleDownload(item.originalIndex, item.attachment.name); }}
          title="Télécharger"
          aria-label="Télécharger"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
